import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { IoArrowBack, IoArrowForward, IoCheckmark } from 'react-icons/io5';

const STEPS = ['currentSituation', 'specificNeeds', 'learningUrgency', 'learningStyle', 'currentKnowledge'];

export default function LearningAdvisor({ onComplete, onCancel, initialProfile = {} }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const [profile, setProfile] = useState({
        currentSituation: initialProfile.currentSituation || '',
        specificNeeds: initialProfile.specificNeeds || '',
        learningUrgency: initialProfile.learningUrgency || '',
        learningStyle: initialProfile.learningStyle || '',
        currentKnowledge: initialProfile.currentKnowledge || '',
    });

    const currentStep = STEPS[step];
    const isLastStep = step === STEPS.length - 1;
    const isTextStep = currentStep === 'specificNeeds';

    const getOptions = (key) => {
        const base = `courseIdeas.questions.${key}.options`;
        const optionKeys = {
            currentSituation: ['career_advancement', 'skill_gap', 'personal_interest', 'academic', 'entrepreneurial'],
            learningUrgency: ['immediate', 'short_term', 'medium_term', 'long_term'],
            learningStyle: ['hands_on', 'theoretical', 'visual', 'social'],
            currentKnowledge: ['complete_beginner', 'basic_knowledge', 'intermediate', 'advanced'],
        };
        return (optionKeys[key] || []).map((id) => ({
            id,
            title: t(`${base}.${id}.title`),
            description: t(`${base}.${id}.description`),
        }));
    };

    const canProceed = () => {
        const val = profile[currentStep];
        return isTextStep ? val.trim().length >= 10 : Boolean(val);
    };

    const handleSelect = (value) => {
        setProfile((prev) => ({ ...prev, [currentStep]: value }));
    };

    const handleNext = () => {
        if (!canProceed()) return;
        if (isLastStep) {
            onComplete(profile);
        } else {
            setStep((s) => s + 1);
        }
    };

    const progress = ((step + 1) / STEPS.length) * 100;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 pt-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('courseIdeas.tabs.learningAdvisor', 'Learning Advisor')}
                    </h2>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        {step + 1} / {STEPS.length}
                    </span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-blue-600 rounded-full"
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            <div className="p-6 min-h-[320px]">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                    >
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">
                            {t(`courseIdeas.questions.${currentStep}.title`)}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                            {t(`courseIdeas.questions.${currentStep}.subtitle`)}
                        </p>

                        {isTextStep ? (
                            <textarea
                                value={profile.specificNeeds}
                                onChange={(e) => handleSelect(e.target.value)}
                                placeholder={t('courseIdeas.questions.specificNeeds.placeholder')}
                                rows={4}
                                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 resize-none text-sm"
                            />
                        ) : (
                            <div className="grid gap-2">
                                {getOptions(currentStep).map((opt) => {
                                    const selected = profile[currentStep] === opt.id;
                                    return (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => handleSelect(opt.id)}
                                            className={`text-start p-4 rounded-lg border-2 transition-all ${
                                                selected
                                                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{opt.title}</p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{opt.description}</p>
                                                </div>
                                                {selected && <IoCheckmark className="w-5 h-5 text-blue-600 shrink-0" />}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <button
                    type="button"
                    onClick={step === 0 ? onCancel : () => setStep((s) => s - 1)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                    <IoArrowBack className="w-4 h-4" />
                    {step === 0
                        ? t('courseIdeas.advisor.cancel', 'Cancel')
                        : t('courseIdeas.advisor.back', 'Back')}
                </button>
                <button
                    type="button"
                    onClick={handleNext}
                    disabled={!canProceed()}
                    className="flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isLastStep
                        ? t('courseIdeas.advisor.generate', 'Generate My Ideas')
                        : t('courseIdeas.advisor.next', 'Continue')}
                    {!isLastStep && <IoArrowForward className="w-4 h-4" />}
                </button>
            </div>
        </div>
    );
}
